import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useMatch, useNavigate, useSearchParams } from 'react-router';

import { PublicLayout } from '@/components/layout/public-layout';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { PasswordField } from '@/components/ui/password-field';
import { TextField } from '@/components/ui/text-field';
import { EMAIL_PATTERN } from '@/lib/validation';

import { login } from '../api/auth-api';
import { captureArrival, readJobId, readSource } from '../arrival';
import { FORM_ERROR, SESSION_EXPIRED, errorMessage } from '../use-auth-error';
import { useSession } from '../use-session';

import { ForgotPasswordDialog } from './forgot-password-dialog';

interface SignInForm {
  username: string;
  password: string;
}

interface LocationState {
  from?: string;
  newPassword?: boolean;
}

export function SignInPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const forgotOpen = useMatch('/sign-in/forgot') !== null;
  const { signIn, signOutReason } = useSession();
  const state = (location.state ?? {}) as LocationState;

  useEffect(() => {
    captureArrival(params);
  }, [params]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitted },
  } = useForm<SignInForm>();

  const mutation = useMutation({
    mutationFn: (v: SignInForm) => login(v.username, v.password, readJobId(), readSource()),
    onSuccess: (token) => {
      signIn(token.access_token);
      void navigate(state.from ?? '/apply', { replace: true });
    },
  });

  const hasFieldErrors = isSubmitted && Object.keys(errors).length > 0;

  return (
    <PublicLayout
      title="Pick up where you left off."
      copy="Sign in and we'll take you straight back to the step you were on."
    >
      <div>
        <h3>Sign in</h3>
        <p className="muted">Use the email and password you created when you started.</p>
      </div>

      {state.newPassword && !hasFieldErrors && (
        <Alert variant="info">Your password has been updated.</Alert>
      )}
      {signOutReason === 'expired' && <Alert variant="info">{SESSION_EXPIRED}</Alert>}
      {mutation.isError && <Alert>{errorMessage(mutation.error)}</Alert>}
      {!mutation.isError && hasFieldErrors && <Alert>{FORM_ERROR}</Alert>}

      <form
        className="stack"
        noValidate
        onSubmit={(e) =>
          void handleSubmit((v) => {
            mutation.mutate(v);
          })(e)
        }
      >
        <TextField
          id="signIn-username"
          label="Email Address"
          type="email"
          inputMode="email"
          autoComplete="username"
          error={errors.username?.message}
          {...register('username', {
            required: 'Please provide a valid email address',
            pattern: { value: EMAIL_PATTERN, message: 'Please provide a valid email address' },
          })}
        />
        <PasswordField
          id="signIn-password"
          label="Password"
          autoComplete="current-password"
          error={errors.password?.message}
          {...register('password', { required: 'Please provide a valid password.' })}
        />
        <p className="field__hint">
          <Link to="/sign-in/forgot">Forgot your password?</Link>
        </p>
        <Button type="submit" block busy={mutation.isPending}>
          Sign In
        </Button>
      </form>

      <hr className="divider divider--gray" />
      <div className="card__switch">
        <p>New to MAS?</p>
        <Link
          to={{ pathname: '/welcome', search: location.search }}
          className="btn btn--secondary btn--block"
        >
          Start your application
        </Link>
      </div>

      <ForgotPasswordDialog open={forgotOpen} onClose={() => void navigate('/sign-in')} />
    </PublicLayout>
  );
}
